import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Badge,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  Paper,
  Tooltip,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  CircularProgress
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  ExpandMore as ExpandMoreIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  EventNote as EventNoteIcon,
  DoneAll as DoneAllIcon,
  OpenInNew as OpenInNewIcon,
  BeachAccess as BeachAccessIcon,
  Campaign as CampaignIcon,
  Assignment as AssignmentIcon,
  ClassOutlined as ClassIcon,
  Balance as BalanceIcon,
  HowToReg as HowToRegIcon,
  Groups as GroupsIcon,
  Search as SearchIcon,
  HelpOutline as HelpOutlineIcon,
  Email as InboxIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { resolveNotificationRoute } from '../utils/notificationRoute';
import GlobalSearch from './GlobalSearch';
import useInboxUnreadCount from '../hooks/useInboxUnreadCount';

const SIDEBAR_ROLES = ['principal', 'school_admin'];
const SEARCH_ROLES = ['principal', 'school_admin'];
const INBOX_ROLES = ['principal', 'school_admin', 'teacher'];

const INBOX_PATHS = {
  principal: '/principal/inbox',
  school_admin: '/principal/inbox',
  teacher: '/teacher/inbox',
};

const ROLE_LABELS = {
  principal: 'Principal',
  school_admin: 'School Admin',
  teacher: 'Teacher',
  parent: 'Parent',
  super_admin: 'Super Admin',
};

const HOME_PATHS = {
  principal: '/principal/dashboard',
  school_admin: '/principal/dashboard',
  teacher: '/teacher/dashboard',
  parent: '/parent/dashboard',
  super_admin: '/superadmin/dashboard',
};

// Only roles with a How-to-Use guide get the header help icon.
const HELP_PATHS = {
  principal: '/principal/help-guide',
  school_admin: '/principal/help-guide',
  teacher: '/teacher/help-guide',
  parent: '/parent/help-guide',
};

// Map notification type to icon + color
const getNotifStyle = (type) => {
  switch (type) {
    case 'leave_approved':
      return { icon: <CheckCircleIcon fontSize="small" />, color: '#10b981', bg: 'rgba(16,185,129,0.1)' };
    case 'leave_rejected':
      return { icon: <CancelIcon fontSize="small" />, color: '#ef4444', bg: 'rgba(239,68,68,0.1)' };
    case 'leave_request':
      return { icon: <EventNoteIcon fontSize="small" />, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' };
    case 'holiday_created':
      return { icon: <BeachAccessIcon fontSize="small" />, color: '#06b6d4', bg: 'rgba(6,182,212,0.1)' };
    case 'announcement_created':
      return { icon: <CampaignIcon fontSize="small" />, color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)' };
    case 'exam_created':
      return { icon: <AssignmentIcon fontSize="small" />, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' };
    case 'class_assigned':
      return { icon: <ClassIcon fontSize="small" />, color: '#2563eb', bg: 'rgba(37,99,235,0.1)' };
    case 'leave_balance_updated':
      return { icon: <BalanceIcon fontSize="small" />, color: '#10b981', bg: 'rgba(16,185,129,0.1)' };
    case 'weekly_plan_submitted':
    case 'marks_submitted':
      return { icon: <AssignmentIcon fontSize="small" />, color: '#6366f1', bg: 'rgba(99,102,241,0.1)' };
    case 'attendance_submitted':
      return { icon: <HowToRegIcon fontSize="small" />, color: '#2563eb', bg: 'rgba(37,99,235,0.1)' };
    case 'meeting_scheduled':
    case 'meeting_completed':
      return { icon: <GroupsIcon fontSize="small" />, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' };
    default:
      return { icon: <NotificationsIcon fontSize="small" />, color: '#667eea', bg: 'rgba(102,126,234,0.1)' };
  }
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const ModernHeader = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifEl, setNotifEl] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);

  const { user, logout } = useAuth();
  const { toggleSidebar } = useSidebar();
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const inboxUnread = useInboxUnreadCount();

  const schoolName = user?.school?.name || 'ClassTree';
  const userName = user?.name;
  const userRole = ROLE_LABELS[user?.role] || 'User';
  const hasSidebar = SIDEBAR_ROLES.includes(user?.role);
  const hasSearch = SEARCH_ROLES.includes(user?.role);
  const helpPath = HELP_PATHS[user?.role];
  const hasInbox = INBOX_ROLES.includes(user?.role);
  const inboxPath = INBOX_PATHS[user?.role];

  useEffect(() => {
    if (!hasSearch) return;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasSearch]);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleNotifOpen = (event) => setNotifEl(event.currentTarget);
  const handleNotifClose = () => setNotifEl(null);

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/login');
  };

  const handleNotifClick = async (notif) => {
    if (!notif.is_read) await markRead(notif.id);
    handleNotifClose();
    const href = resolveNotificationRoute(notif, user?.role);
    if (href) navigate(href);
  };

  const handleLogoClick = () => navigate(HOME_PATHS[user?.role] || '/');

  const handleViewAll = () => {
    handleNotifClose();
    const role = user?.role;
    if (role === 'teacher') navigate('/teacher/notifications');
    else if (role === 'parent') navigate('/parent/notifications');
    else navigate('/principal/notifications');
  };

  const handleMarkAllRead = async (e) => {
    e.stopPropagation();
    await markAllRead();
  };

  // Role-based badge color
  const getRoleColor = () => {
    switch (user?.role) {
      case 'principal': return 'error';
      case 'teacher': return 'success';
      case 'super_admin': return 'warning';
      case 'school_admin': return 'info';
      default: return 'default';
    }
  };

  // Role-based avatar background
  const getRoleBackground = () => {
    switch (user?.role) {
      case 'principal': return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      case 'teacher': return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
      case 'super_admin': return 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
      case 'school_admin': return 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)';
      default: return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    }
  };

  const abbreviation = userName
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  const previewNotifs = notifications.slice(0, 5);

  return (
    <>
      {/* Modern AppBar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          transition: 'all 0.3s ease',
          margin: 0,
          padding: 0,
          width: '100%',
          borderRadius: '0px',
          zIndex: 1200,
          '&:hover': {
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
          }
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1.5,
            px: { xs: 2, sm: 3 },
          }}
        >
          {/* Left Section - Logo and School Info */}
          <Box display="flex" alignItems="center" gap={2} sx={{ minWidth: 0, flex: 1 }}>
            {isMobile && hasSidebar && (
              <IconButton color="inherit" onClick={toggleSidebar} edge="start" sx={{ mr: -0.5 }}>
                <MenuIcon />
              </IconButton>
            )}
            <Tooltip title="Go to Dashboard">
              <Box
                onClick={handleLogoClick}
                display="flex"
                alignItems="center"
                gap={2}
                sx={{ cursor: 'pointer', minWidth: 0 }}
              >
                <Box
                  component="img"
                  src="/assets/logo.png"
                  alt="ClassTree"
                  sx={{
                    width: 42, height: 42, borderRadius: '8px', objectFit: 'contain',
                    background: 'rgba(255, 255, 255, 0.9)', padding: '3px',
                    transition: 'all 0.3s ease', '&:hover': { transform: 'scale(1.05)' }
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{
                    color: 'white', fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', letterSpacing: '0.3px',
                  }}>
                    {schoolName || 'ClassTree'}
                  </Typography>
                  <Typography variant="caption" sx={{
                    color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.75rem',
                    textTransform: 'uppercase', letterSpacing: '1px',
                  }}>
                    ClassTree.in
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          </Box>

          {/* Center Section - Home (Desktop only) */}
          {!isMobile && (
            <Tooltip title="Home">
              <Box
                onClick={handleLogoClick}
                sx={{
                  mx: 'auto', display: 'flex', alignItems: 'center', gap: 0.75,
                  bgcolor: 'rgba(255, 255, 255, 0.15)', borderRadius: '20px',
                  px: 2, py: 0.5, border: '1px solid rgba(255, 255, 255, 0.2)',
                  cursor: 'pointer', transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.22)' },
                }}
              >
                <HomeIcon sx={{ fontSize: '1.1rem', color: '#ffffff' }} />
                <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.3px' }}>
                  Home
                </Typography>
              </Box>
            </Tooltip>
          )}

          {/* Right Section - Actions & User Menu */}
          <Box display="flex" alignItems="center" gap={1}>
            {/* Inbox — desktop only; mobile top bar is intentionally reduced to
                just the alert bell + avatar (Inbox stays reachable via the
                sidebar/dashboard quick actions on mobile) */}
            {hasInbox && !isMobile && (
              <Tooltip title="Inbox">
                <IconButton
                  color="inherit"
                  onClick={() => navigate(inboxPath)}
                  sx={{
                    transition: 'all 0.3s ease',
                    '&:hover': { background: 'rgba(255, 255, 255, 0.1)', transform: 'scale(1.1)' }
                  }}
                >
                  <Badge
                    badgeContent={inboxUnread || 0}
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        color: 'white', fontWeight: 700,
                      }
                    }}
                  >
                    <InboxIcon sx={{ fontSize: '1.7rem' }} />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}

            {/* Global Search — desktop only; Ctrl/Cmd+K remains the primary
                entry point there, mobile top bar stays reduced to the bell */}
            {hasSearch && !isMobile && (
              <Tooltip title="Search (Ctrl+K)">
                <Box
                  onClick={() => setSearchOpen(true)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75,
                    bgcolor: 'rgba(255, 255, 255, 0.15)', borderRadius: '20px',
                    px: 1.5, py: 0.5, border: '1px solid rgba(255, 255, 255, 0.2)',
                    cursor: 'pointer', transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.22)' },
                  }}
                >
                  <SearchIcon sx={{ fontSize: '1.1rem', color: '#ffffff' }} />
                  <Typography sx={{ color: '#ffffff', fontWeight: 600, fontSize: '0.8rem' }}>
                    Search
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '4px', px: 0.5 }}>
                    Ctrl+K
                  </Typography>
                </Box>
              </Tooltip>
            )}

            {/* Help / User Guide — desktop only; reachable from the sidebar on mobile */}
            {helpPath && !isMobile && (
              <Tooltip title="How to Use">
                <IconButton
                  color="inherit"
                  onClick={() => navigate(helpPath)}
                  sx={{
                    transition: 'all 0.3s ease',
                    '&:hover': { background: 'rgba(255, 255, 255, 0.1)', transform: 'scale(1.1)' }
                  }}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Notification Bell — the one action kept on the mobile top bar */}
            <Tooltip title="Notifications">
              <IconButton
                color="inherit"
                onClick={handleNotifOpen}
                sx={{
                  transition: 'all 0.3s ease',
                  '&:hover': { background: 'rgba(255, 255, 255, 0.1)', transform: 'scale(1.1)' }
                }}
              >
                <Badge
                  badgeContent={unreadCount || 0}
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      color: 'white', fontWeight: 700,
                    }
                  }}
                >
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Notifications Dropdown */}
            <Menu
              anchorEl={notifEl}
              open={Boolean(notifEl)}
              onClose={handleNotifClose}
              PaperProps={{
                sx: {
                  backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.97) 0%, rgba(255,255,255,0.93) 100%)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 8px 32px rgba(31, 38, 135, 0.37)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  mt: 1.5,
                  width: 360,
                  maxWidth: '95vw',
                  overflow: 'hidden',
                }
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {/* Header row */}
              <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                  Notifications {unreadCount > 0 && (
                    <Chip label={unreadCount} size="small" color="error" sx={{ ml: 0.5, height: 18, fontSize: '0.7rem', fontWeight: 700 }} />
                  )}
                </Typography>
                {unreadCount > 0 && (
                  <Tooltip title="Mark all as read">
                    <IconButton size="small" onClick={handleMarkAllRead} sx={{ color: '#667eea' }}>
                      <DoneAllIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {/* Notification list */}
              {previewNotifs.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <NotificationsIcon sx={{ fontSize: 40, color: 'rgba(0,0,0,0.15)', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">No notifications yet</Typography>
                </Box>
              ) : (
                <List disablePadding sx={{ maxHeight: 320, overflowY: 'auto' }}>
                  {previewNotifs.map((notif) => {
                    const style = getNotifStyle(notif.type);
                    return (
                      <ListItem
                        key={notif.id}
                        button
                        onClick={() => handleNotifClick(notif)}
                        sx={{
                          px: 2, py: 1.2,
                          bgcolor: notif.is_read ? 'transparent' : 'rgba(102,126,234,0.05)',
                          borderBottom: '1px solid rgba(0,0,0,0.04)',
                          transition: 'background 0.2s',
                          '&:hover': { bgcolor: 'rgba(102,126,234,0.08)' },
                          alignItems: 'flex-start',
                        }}
                      >
                        <ListItemAvatar sx={{ minWidth: 40, mt: 0.5 }}>
                          <Box sx={{
                            width: 32, height: 32, borderRadius: '50%',
                            bgcolor: style.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: style.color,
                          }}>
                            {style.icon}
                          </Box>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography variant="body2" fontWeight={notif.is_read ? 500 : 700} sx={{ fontSize: '0.82rem', flex: 1 }}>
                                {notif.title}
                              </Typography>
                              {!notif.is_read && (
                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#667eea', flexShrink: 0 }} />
                              )}
                            </Box>
                          }
                          secondary={
                            <>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4, fontSize: '0.76rem' }}>
                                {notif.message}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.71rem' }}>
                                {timeAgo(notif.created_at)}
                              </Typography>
                            </>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}

              {/* Footer */}
              <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.06)', px: 2, py: 1 }}>
                <Button
                  fullWidth
                  size="small"
                  endIcon={<OpenInNewIcon fontSize="small" />}
                  onClick={handleViewAll}
                  sx={{ color: '#667eea', fontWeight: 600, fontSize: '0.8rem', textTransform: 'none' }}
                >
                  View all notifications
                </Button>
              </Box>
            </Menu>

            {/* Divider */}
            <Divider flexItem orientation="vertical" sx={{ my: 1, bgcolor: 'rgba(255,255,255,0.2)' }} />

            {/* User Profile Section */}
            <Box
              onClick={handleMenuOpen}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 1.5, py: 0.5, borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                cursor: 'pointer', transition: 'all 0.3s ease',
                '&:hover': { background: 'rgba(255, 255, 255, 0.15)', borderColor: 'rgba(255, 255, 255, 0.3)', transform: 'translateY(-2px)' }
              }}
            >
              <Avatar sx={{ width: 36, height: 36, background: getRoleBackground(), fontSize: '0.875rem', fontWeight: 700, border: '2px solid rgba(255, 255, 255, 0.5)' }}>
                {abbreviation}
              </Avatar>
              {!isMobile && (
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {userName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', textTransform: 'capitalize' }}>
                    {userRole?.replace('_', ' ')}
                  </Typography>
                </Box>
              )}
              <ExpandMoreIcon sx={{ fontSize: '1.2rem' }} />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* User Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.95) 100%)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(31, 38, 135, 0.37)',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            mt: 1.5, overflow: 'visible', width: 280,
          }
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* User Info Section */}
        <Box sx={{ px: 2, py: 2 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ width: 48, height: 48, background: getRoleBackground(), fontSize: '1rem', fontWeight: 700 }}>
              {abbreviation}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#333' }}>{userName}</Typography>
              <Chip
                label={userRole?.replace('_', ' ').toUpperCase()}
                size="small"
                color={getRoleColor()}
                variant="outlined"
                sx={{ mt: 0.5, height: 24, fontSize: '0.7rem', fontWeight: 600, textTransform: 'capitalize' }}
              />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 1, background: 'rgba(0,0,0,0.08)' }} />

        <MenuItem
          onClick={handleLogout}
          sx={{ mx: 1, my: 0.5, borderRadius: '6px', transition: 'all 0.2s ease', '&:hover': { background: 'rgba(245, 87, 108, 0.1)' } }}
        >
          <LogoutIcon fontSize="small" sx={{ mr: 1.5, color: '#f5576c' }} />
          <Typography variant="body2" sx={{ color: '#f5576c', fontWeight: 600 }}>Logout</Typography>
        </MenuItem>
      </Menu>

      {hasSearch && (
        <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      )}
    </>
  );
};

export default ModernHeader;
